/** Back-testing state. The server is the system of record; these atoms are the working copy. */

import { atom } from 'jotai';
import type { ScenarioTypeBranch } from '@/services/ai-function.service';
import type { Scenario } from './types';

/**
 * The scenarios of whichever types have been opened. In memory only: they carry
 * whole customer threads, heavy enough that a handful of SOPs would cross a
 * storage quota, and the server is the record either way.
 */
export const scenariosBySopAtom = atom<Record<string, Scenario[]>>({});

/** The outcome of the selected scenario type; the protocol viewer lights its decision paths in the text and the graph. */
export const activeBranchAtom = atom<ScenarioTypeBranch | null>(null);

/** Selects the past tickets placed under no scenario type; they are not a type, so not a type id. */
export const UNTYPED_TYPE_ID = 'none';

/** Types whose scenarios are in memory, so opening one again costs nothing. */
export const loadedTypesBySopAtom = atom<Record<string, string[]>>({});

/** SOPs kept in memory at once; the ones being read are reloaded from the server anyway. */
const MAX_CACHED_SOPS = 5;

const withinCap = (bySop: Record<string, Scenario[]>, keep: string): Record<string, Scenario[]> => {
	const ids = Object.keys(bySop);
	if (ids.length <= MAX_CACHED_SOPS) return bySop;
	const dropped = ids.filter((id) => id !== keep).slice(0, ids.length - MAX_CACHED_SOPS);
	const next = { ...bySop };
	for (const id of dropped) delete next[id];
	return next;
};

export const removeScenarioAtom = atom(null, (get, set, sopId: string, scenarioId: string) => {
	const all = get(scenariosBySopAtom);
	set(scenariosBySopAtom, { ...all, [sopId]: (all[sopId] ?? []).filter((s) => s.id !== scenarioId) });
});

/** Wipes every scenario held for one SOP; its types live in a query and are cleared there. */
export const clearSopAtom = atom(null, (get, set, sopId: string) => {
	const scenarios = { ...get(scenariosBySopAtom) };
	delete scenarios[sopId];
	set(scenariosBySopAtom, scenarios);
	const loaded = { ...get(loadedTypesBySopAtom) };
	delete loaded[sopId];
	set(loadedTypesBySopAtom, loaded);
});

/**
 * Forgets which types have been loaded, keeping the rows on screen, so the next
 * open refetches. For after a bucketing pass, which places tickets under types
 * nobody has opened yet.
 */
export const invalidateLoadedTypesAtom = atom(null, (get, set, sopId: string) => {
	const loaded = { ...get(loadedTypesBySopAtom) };
	delete loaded[sopId];
	set(loadedTypesBySopAtom, loaded);
});

/**
 * Scenarios just written on the server, such as fabricated ones, after the rows already held. Leaves
 * the loaded marks alone: a type not yet opened still reads its whole list when it is.
 */
export const addScenariosAtom = atom(null, (get, set, sopId: string, scenarios: Scenario[]) => {
	const all = get(scenariosBySopAtom);
	const held = all[sopId] ?? [];
	const fresh = scenarios.filter((scenario) => !held.some((s) => s.id === scenario.id));
	set(scenariosBySopAtom, withinCap({ ...all, [sopId]: [...held, ...fresh] }, sopId));
});

/** One type's scenarios, replacing that type's rows and marking it loaded. */
export const setScenariosForTypeAtom = atom(
	null,
	(get, set, sopId: string, scenarioTypeId: string | null, scenarios: Scenario[]) => {
		const all = get(scenariosBySopAtom);
		const others = (all[sopId] ?? []).filter((s) => s.scenarioTypeId !== scenarioTypeId);
		set(scenariosBySopAtom, withinCap({ ...all, [sopId]: [...others, ...scenarios] }, sopId));
		const key = scenarioTypeId ?? UNTYPED_TYPE_ID;
		const loaded = get(loadedTypesBySopAtom);
		set(loadedTypesBySopAtom, { ...loaded, [sopId]: [...new Set([...(loaded[sopId] ?? []), key])] });
	}
);
